import React, { useEffect, useState } from "react";
import axios from "../../API/axios";
import { getOwnInfo, invalidateOwnInfo, invalidateUserConfig } from "../../Services/apiService";
import { formatDate } from "../../utility/date";
import Flatpickr from "react-flatpickr";
import ReactSelect from "react-select";
import Select from "react-select";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { Loader } from "react-feather";
import SuccessToast from "../SuccessToast";
import WarningToast from "../WarningToast";
import ErrorToast from "../ErrorToast";
import { customStyles } from "../../selectStyles";
import { useTranslation } from "react-i18next";
import useEscapeKeyDown from "../../hooks/useEscapeKeyDown";

const ProfileBasicInfoModal = ({ onClose, getUserProfile, initialProfileInfo }) => {
  const { t } = useTranslation();
  useEscapeKeyDown(true, onClose);
  const sessionId = localStorage.getItem("sessionId");
  const language = localStorage.getItem("Lang");
  const langKey = language === "en" ? "" : language;

  const [isPrefetching, setIsPrefetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [genderValue, setGenderValue] = useState(null);
  const [genderOptionList, setGenderOptionList] = useState([]);
  const [countryData, setCountryData] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [cityData, setCityData] = useState([]);
  const [cityId, setCityId] = useState(null);
  const [citySearch, setCitySearch] = useState("");
  const [websiteContent, setWebsiteContent] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [aboutContent, setAboutContent] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [warningToastMessage, setWarningToastMessage] = useState("");
  const [showWarningToast, setShowWarningToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);

  const genderOptions = genderOptionList.map((val) => ({
    value: val.gndrId,
    label: val.name,
    id: val.gndrId,
  }));

  const isValidWebsiteUrl = (value) => {
    const trimmedValue = value.trim();
    if (!trimmedValue) return true;

    try {
      const normalizedValue = /^https?:\/\//i.test(trimmedValue)
        ? trimmedValue
        : `https://${trimmedValue}`;
      const parsedUrl = new URL(normalizedValue);
      return Boolean(parsedUrl.hostname && parsedUrl.hostname.includes("."));
    } catch {
      return false;
    }
  };

  const validateWebsite = (value) => {
    if (value.trim() !== "" && !isValidWebsiteUrl(value)) {
      setWebsiteError("Please provide a valid website URL.");
    } else {
      setWebsiteError("");
    }
    setWebsiteContent(value);
  };

  const getGenderData = () => {
    axios
      .get(`/Master/GetGenderDDL?LangKey=${language}`)
      .then((response) => {
        setGenderOptionList(response.data.responseData || []);
      })
      .catch(console.error);
  };

  const getCountry = () => {
    axios
      .get(
        `Master/GetCountryDDL?search=${countrySearch || ""}&LangKey=${language}`
      )
      .then((response) => {
        const res = response.data.responseData || [];
        setCountryData(
          res.map((item) => ({
            value: item.countyId,
            label: item.countryName,
            code: item.phoneCode,
          }))
        );
      })
      .catch(console.error);
  };

  const getCity = () => {
    axios
      .get(
        `Master/GetCitiesDDL?search=${citySearch || ""}&CountryID=${countryId || ""}&LangKey=${language}`
      )
      .then((response) => {
        const res = response.data.responseData || [];
        setCityData(
          res.map((item) => ({
            value: item.cityId,
            label: item.cityName,
          }))
        );
      })
      .catch(console.error);
  };

  const getWebsiteValue = (info = {}) =>
    info?.website ?? info?.websiteUrl ?? info?.userWebsite ?? "";

  const getAboutValue = (info = {}) =>
    info?.about ?? info?.aboutContent ?? info?.userAbout ?? "";

  const applyProfileInfo = (info = {}) => {
    const dobValue = info?.dob;
    setStartDate(dobValue ? new Date(dobValue) : null);
    setGenderValue(info?.genderId || null);
    setCityId(info?.cityID || null);
    setCitySearch(info?.cityName || "");
    setCountryId(info?.countryID || "");
    setCountrySearch(info?.countryName || "");
    if (info?.countryID && info?.countryName) {
      setCountryData([
        {
          value: info.countryID,
          label: info.countryName,
          code: info.phoneCode || "",
        },
      ]);
    }
    if (info?.cityID && info?.cityName) {
      setCityData([
        {
          value: info.cityID,
          label: info.cityName,
        },
      ]);
    }
    const website = getWebsiteValue(info);
    const about = getAboutValue(info);
    setWebsiteContent((prev) => website || prev);
    setAboutContent((prev) => about || prev);
    setFirstName(info?.firstName || "");
    setLastName(info?.lastName || "");
    setPhoneNumber(info?.phoneNumber || "");
    setWebsiteError("");
  };

  const fetchProfileWebsiteAbout = async () => {
    const userUid = localStorage.getItem("userUid");
    if (!userUid || !sessionId) return {};

    try {
      const response = await axios.post("UserProfile/GetUserProfile", {
        deviceType: "Web",
        userUid,
        langKey: language,
      });
      return response.data?.responseData?.profileInfo || {};
    } catch (error) {
      console.error("Error fetching profile website/about:", error);
      return {};
    }
  };

  const prefetchFormData = async () => {
    setIsPrefetching(true);
    try {
      const [ownInfo, profileInfo] = await Promise.all([
        getOwnInfo({ forceRefresh: true }),
        fetchProfileWebsiteAbout(),
      ]);

      applyProfileInfo({
        ...(ownInfo || {}),
        website:
          getWebsiteValue(ownInfo) ||
          getWebsiteValue(profileInfo) ||
          getWebsiteValue(initialProfileInfo),
        about:
          getAboutValue(ownInfo) ||
          getAboutValue(profileInfo) ||
          getAboutValue(initialProfileInfo),
      });
    } catch (error) {
      console.error("Error prefetching profile basic info:", error);
    } finally {
      setIsPrefetching(false);
    }
  };

  useEffect(() => {
    getGenderData();
    if (initialProfileInfo) {
      applyProfileInfo(initialProfileInfo);
    }
    const prefetchTimer = setTimeout(() => {
      prefetchFormData();
    }, 0);

    return () => clearTimeout(prefetchTimer);
  }, []);

  useEffect(() => {
    if (countrySearch) {
      getCountry();
    }
  }, [countrySearch]);

  useEffect(() => {
    if (countryId || citySearch) {
      getCity();
    } else {
      setCityData([]);
    }
  }, [countryId, citySearch]);

  const preventYearScroll = (datePickerInstance) => {
    const yearElement = datePickerInstance.currentYearElement;
    if (yearElement) {
      yearElement.addEventListener("wheel", (e) => {
        e.preventDefault();
      });
    }
  };

  const appendBaseFields = (formData) => {
    formData.append("DeviceType", "Web");
    formData.append("sessionId", sessionId);
    formData.append("Language", langKey);
  };

  const getApiErrorMessage = (error) => {
    const responseData = error?.response?.data;

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message.trim();
    }

    const validationErrors = responseData?.errors;
    if (validationErrors && typeof validationErrors === "object") {
      const firstValidationMessage = Object.values(validationErrors)
        .flat()
        .find((message) => typeof message === "string" && message.trim());

      if (firstValidationMessage) {
        return firstValidationMessage.trim();
      }
    }

    if (typeof responseData?.title === "string" && responseData.title.trim()) {
      return responseData.title.trim();
    }

    return "Something went wrong.";
  };

  const handleApiError = (error) => {
    if (error?.response?.status === 404) {
      setWarningToastMessage(getApiErrorMessage(error));
      setShowWarningToast(true);
    } else {
      setErrorToastMessage(getApiErrorMessage(error));
      setShowErrorToast(true);
    }
  };

  const handleSave = async () => {
    if (
      !startDate ||
      !countryId ||
      !genderValue ||
      !firstName ||
      !lastName ||
      !cityId
    ) {
      setWarningToastMessage(t("modals.Please fill all the required fields"));
      setShowWarningToast(true);
      return;
    }

    if (websiteContent.trim() !== "" && !isValidWebsiteUrl(websiteContent)) {
      setWarningToastMessage("Please provide a valid website URL.");
      setShowWarningToast(true);
      return;
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("DeviceType", "Web");
      formData.append("sessionId", sessionId);
      formData.append("Dob", formatDate(startDate, "YYYY-MM-DD"));
      formData.append("CityID", cityId);
      formData.append("GenderId", genderValue);
      formData.append("LastName", lastName);
      formData.append("PhoneNumber", phoneNumber);
      formData.append("CntryID", countryId);
      formData.append("FirstName", firstName);
      formData.append("Language", langKey);

      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);

      const res = await axios.post("/UserProfile/UpdateCompleteInfo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      let lastMessage = res.data.message;

      if (websiteContent.trim()) {
        const websiteForm = new FormData();
        appendBaseFields(websiteForm);
        websiteForm.append("Website", websiteContent.trim());
        const websiteRes = await axios.post("/UserAccount/SetWebsite", websiteForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastMessage = websiteRes.data.message;
      }

      if ((aboutContent || "").trim()) {
        const aboutForm = new FormData();
        appendBaseFields(aboutForm);
        aboutForm.append("AboutContent", aboutContent.trim());
        const aboutRes = await axios.post("/UserAccount/SetAbout", aboutForm, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        lastMessage = aboutRes.data.message;
      }

      setToastMessage(lastMessage);
      setShowToast(true);
      invalidateOwnInfo();
      invalidateUserConfig();
      await getUserProfile();
      onClose();
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className="modal fade show postAdd"
        tabIndex="-1"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div className="modal-content d-flex justify-content-around overflow-hidden">
            <div className="modalBorder d-flex justify-content-between align-items-center p-2">
              <h6>{t("User Profile.Profile Basic Info")}</h6>
              <CloseSharpIcon
                className="material-symbols-sharp me-0 pointer"
                onClick={onClose}
              />
            </div>

            <div className="p-3" style={{ minHeight: "420px" }}>
                <div className="row">
                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Date Of Birth")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="dateofbirth w-100">
                      <Flatpickr
                        className="form-control"
                        value={startDate}
                        onChange={(date) => setStartDate(date[0])}
                        placeholder="Select Date"
                        options={{
                          maxDate: "today",
                          onReady: (_selectedDates, _dateStr, instance) => {
                            preventYearScroll(instance);
                          },
                          onOpen: (_selectedDates, _dateStr, instance) => {
                            preventYearScroll(instance);
                          },
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Gender")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <ReactSelect
                      className="NewGenderAdd"
                      value={genderOptions.find(
                        (option) => option.value === genderValue
                      )}
                      options={genderOptions}
                      onChange={(val) => setGenderValue(val?.value || null)}
                      placeholder={t("Newsfeed.Select Gender")}
                      styles={customStyles}
                      classNamePrefix="react-select"
                    />
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.Country")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      value={countryData.find(
                        (option) => option.value === countryId
                      )}
                      isClearable
                      options={countryData}
                      onChange={(selectedOption) => {
                        setCountryId(selectedOption ? selectedOption.value : "");
                        setCityId(null);
                      }}
                      placeholder={t("SearchJob.Select Country")}
                      onInputChange={(inputValue) => setCountrySearch(inputValue)}
                      styles={customStyles}
                    />
                  </div>

                  <div className="col-md-6 mb-3 userInput">
                    <label className="f-14 mb-1">
                      {t("User Profile.City")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      value={cityData.find((option) => option.value === cityId)}
                      onChange={(selectedOption) =>
                        setCityId(selectedOption ? selectedOption.value : null)
                      }
                      options={cityData}
                      noOptionsMessage={() => t("modals.Search and Select City")}
                      placeholder="Select City"
                      onInputChange={(inputValue) => setCitySearch(inputValue)}
                      styles={customStyles}
                      isClearable
                    />
                  </div>

                  <div className="col-md-12 mb-3 userInput">
                    <label className="f-14 mb-1">{t("User Profile.Website")}</label>
                    <div className="web-input">
                      <div className="input-group input-group-alt">
                        <div className="input-group-prepend">
                          <span className="input-group-text">http://</span>
                        </div>
                        <input
                          type="text"
                          className="form-control pb-0 px-2"
                          placeholder="example.com"
                          value={websiteContent}
                          onChange={(e) => validateWebsite(e.target.value)}
                        />
                      </div>
                      {websiteError && (
                        <div
                          style={{
                            color: "red",
                            marginTop: "5px",
                            fontSize: "12px",
                          }}
                        >
                          {websiteError}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="col-md-12 mb-2 userInput about-textArea">
                    <label className="f-14 mb-1">
                      {t("User Profile.About")}
                      {/* <span className="text-danger ms-1">*</span> */}
                    </label>
                    <textarea
                      rows="4"
                      cols="55"
                      value={aboutContent}
                      onChange={(e) => setAboutContent(e.target.value)}
                      maxLength={500}
                    />
                    <p className="info">{aboutContent?.length || 0}/500</p>
                  </div>
                </div>

                {aboutContent.length >= 500 && (
                  <p className="text-danger f-14 mb-0">
                    {t("User Profile.About content should be less than 500 words")}
                  </p>
                )}

                <div className="col-md-12 text-end mt-3 mb-2 d-flex justify-content-end align-items-center">
                  {isPrefetching && (
                    <Loader className="spinner me-2" size={18} />
                  )}
                  <button
                    type="button"
                    className="mainBtn1 me-2"
                    onClick={handleSave}
                    disabled={isPrefetching || isSaving || !!websiteError}
                    style={{ minWidth: "90px" }}
                  >
                    {isSaving ? t("User Profile.Submit") : t("User Profile.Submit")}
                  </button>
                </div>
              </div>
          </div>
        </div>
      </div>

      <SuccessToast
        show={showToast}
        message={toastMessage}
        onClose={() => setShowToast(false)}
      />
      <WarningToast
        show={showWarningToast}
        message={warningToastMessage}
        onClose={() => setShowWarningToast(false)}
      />
      <ErrorToast
        show={showErrorToast}
        message={errorToastMessage}
        onClose={() => setShowErrorToast(false)}
      />
    </>
  );
};

export default ProfileBasicInfoModal;
